import * as React from 'react';
import { Link } from 'react-router';
import styled, { withTheme } from 'styled-components';
import { rem, rgba } from 'polished';
import { FlexCol } from '../../Defaults/Flexbox/Flexbox';
import { SvgIcon, IconType } from '../General/SvgIcon';
import { __ } from 'react-i18n';

export interface Props {
  url: string;
  title: string;
  imageSrc: string;
  alt?: string;
  buttonColor?: string;
  bgColor?: string;
  size: number;
  nonWebpImage: string;
}

function Card({
  url,
  title,
  alt,
  buttonColor,
  bgColor,
  size,
  imageSrc,
  nonWebpImage,
}: Props) {
  const renderSymbol = (): JSX.Element => {
    if (size === 2) {
      return (
        <BigStyledSvgIcon
          icon={IconType.logoSymbol}
          viewBox="0 0 50 50"
          width={571}
          height={545}
        />
      );
    } else {
      return (
        <StyledSvgIcon
          icon={IconType.logoSymbol}
          viewBox="0 0 50 50"
          width={491}
          height={469}
        />
      );
    }
  };

  return (
    <ImageWrapper
      backgroundUrl={imageSrc}
      nonWebpImage={nonWebpImage}
      spanSize={size}
    >
      {imageSrc !== null && renderSymbol()}
      <TextButtonWrapper spanSize={size}>
        <TextLink to={url} spanSize={size}>
          {title}
        </TextLink>
        <ButtonWrapper>
          <CardButton to={url} spanSize={size}>
            {__('Zobraziť')}
          </CardButton>
          <SvgIcon
            width={16}
            height={16}
            viewBox={'0 0 16 16'}
            icon={IconType.rightArrow}
            fill={bgColor}
            cursor={'pointer'}
            marginLeft={8}
          />
        </ButtonWrapper>
      </TextButtonWrapper>
    </ImageWrapper>
  );
}

const ImageWrapper = styled.section<{
  backgroundUrl: string;
  spanSize: number;
  nonWebpImage: string;
}>`
  width: 100%;
  position: relative;
  background-color: ${({ theme }) => theme.colors.primaryBackground};
  background-image: url(${props => props.backgroundUrl});
  background-size: cover;
  border-radius: ${rem(8)};
  overflow: hidden;
  grid-row: span ${props => props.spanSize};

  padding: ${props => (props.spanSize === 1 ? `${rem(48)}` : `${rem(56)}`)};

  .no-webp &&& {
    background-image: url('${props => props.nonWebpImage}');
  }

  ${({ theme }) => theme.mediab.m810`
    grid-row: span 1;
    padding: ${props => (props.spanSize === 1 ? `${rem(24)}` : `${rem(32)}`)};
  `}
`;

const TextButtonWrapper = styled(FlexCol)<{ spanSize: number }>`
  justify-content: ${props => (props.spanSize === 1 ? 'center' : 'flex-end')};
  align-items: flex-start;
  height: 100%;
`;

const ButtonWrapper = styled(FlexCol)`
  display: inline-block;
`;

const TextLink = styled(Link)<{ spanSize: number }>`
  text-decoration: none;
  font-size: ${props => (props.spanSize === 1 ? `${rem(30)}` : `${rem(48)}`)};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: ${props => (props.spanSize === 1 ? `${rem(35)}` : `${rem(55)}`)};
  font-family: ${({ theme }) => theme.font.secondary};
  font-weight: 500;
  margin: 0;
  margin-bottom: ${props =>
    props.spanSize === 1 ? `${rem(16)}` : `${rem(24)}`};

  ${({ theme }) => theme.mediab.m810`
    grid-row: span 1;
    font-size: ${props => (props.spanSize === 1 ? `${rem(24)}` : `${rem(32)}`)};
    line-height: ${props =>
      props.spanSize === 1 ? `${rem(28)}` : `${rem(35)}`};
  `}
`;

const CardButton = styled(Link)<{ spanSize: number }>`
  display: inline-block;
  color: ${({ theme }) => theme.colors.primary};
  border: none;
  margin-bottom: 0;
  font-weight: 500;
  text-decoration: none;
  font-family: ${({ theme }) => theme.font.primary};

  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

const StyledSvgIcon = styled(SvgIcon)`
  position: absolute;
  left: 50%;
  bottom: -25%;
  fill: ${({ theme }) => rgba(theme.colors.primary, 0.16)};

  * {
    fill: inherit;
  }

  ${({ theme }) => theme.media(0, 'sm')} {
    width: ${rem(434)};
    height: ${rem(419)};
    left: ${rem(103)};
  }
`;

const BigStyledSvgIcon = styled(StyledSvgIcon)`
  left: -110px;
  bottom: 35%;
`;

export { Card };
